import { useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";

const D = OPS.sc.reasonDialog;
export const REASON_MIN = 5;

/** A required-reason confirm (finance «إلغاء الاعتماد»). Copies RecordDelete:
 *  the reason is cleared every time the dialog opens, confirm stays disabled
 *  until it has 5 characters (the server's SC_REASON_REQUIRED rule), and the
 *  dialog cannot be dismissed while the request runs. `onConfirm(reason)`
 *  returns a promise; the caller closes the dialog on success. */
export function ReasonDialog({ open, onOpenChange, title, description, confirmLabel, destructive, onConfirm }) {
    const { t } = useI18n();
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setReason(""); }, [open]);
    const valid = reason.trim().length >= REASON_MIN;

    const confirm = async () => {
        setBusy(true);
        try {
            await onConfirm(reason.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
            <DialogContent data-testid={D.dialog} className="bg-[color:var(--bg-surface)] sm:max-w-md">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description || t("stk_admin_reason_hint")}</DialogDescription>
                </DialogHeader>
                <label className="space-y-1.5 text-sm font-semibold text-[color:var(--ink)]">
                    <span>{t("stk_admin_reason")}</span>
                    <Textarea data-testid={D.reason} value={reason} maxLength={500} rows={3}
                              onChange={(e) => setReason(e.target.value)} className="text-base" />
                    <span className="block text-[11px] font-normal text-[color:var(--ink-faint)]">{t("stk_admin_reason_min")}</span>
                </label>
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={D.cancel} disabled={busy} onClick={() => onOpenChange(false)}>
                        {t("stk_admin_cancel")}
                    </Button>
                    <Button type="button" variant={destructive ? "destructive" : "default"} data-testid={D.confirm}
                            disabled={!valid || busy} onClick={confirm}>
                        {busy ? t("stk_admin_saving") : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
